import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  CircleHelp,
  Clock3,
  ExternalLink,
  Headphones,
  Inbox,
  LoaderCircle,
  Mail,
  MapPin,
  MessageSquareWarning,
  Phone,
  Plus,
  RadioTower,
  RefreshCw,
  Search,
  Send,
  StickyNote,
  UserRound,
  X,
} from "lucide-react";
import api from "../api/api";
import "./SupportCenter.css";

const INSTALLATION_STATUSES = [
  "new",
  "contacted",
  "scheduled",
  "installed",
  "cancelled",
];
const RECLAMATION_STATUSES = ["new", "in_progress", "resolved", "closed"];

const SupportCenter = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("installation");
  const [installations, setInstallations] = useState([]);
  const [reclamations, setReclamations] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageLoading, setMessageLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftStatus, setDraftStatus] = useState("new");
  const [scheduledAt, setScheduledAt] = useState("");
  const [faqFormOpen, setFaqFormOpen] = useState(false);
  const [faqDraft, setFaqDraft] = useState({ question: "", answer: "" });
  const [faqSaving, setFaqSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const isRtl = i18n.dir() === "rtl";

  const showNotice = useCallback((type, text) => {
    setNotice({ type, text });
    window.setTimeout(() => setNotice(null), 3500);
  }, []);

  const loadData = useCallback(
    async (quiet = false) => {
      quiet ? setRefreshing(true) : setLoading(true);
      try {
        const [ticketsResponse, faqResponse] = await Promise.all([
          api.get("/admin/support/tickets"),
          api.get("/admin/faqs"),
        ]);
        setInstallations(ticketsResponse.data?.installations || []);
        setReclamations(ticketsResponse.data?.reclamations || []);
        setFaqs(faqResponse.data?.data || []);
      } catch (error) {
        console.error("Support center loading failed:", error);
        showNotice(
          "error",
          t(
            "support_load_error",
            "Impossible de charger le centre de support.",
          ),
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [showNotice, t],
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  const counters = useMemo(
    () => ({
      installation: installations.filter(
        (item) => !["installed", "cancelled"].includes(item.workflow_status),
      ).length,
      reclamation: reclamations.filter(
        (item) => !["resolved", "closed"].includes(item.support_status),
      ).length,
      faq: faqs.filter((item) => item.is_active).length,
    }),
    [faqs, installations, reclamations],
  );

  const statusOptions =
    activeTab === "installation" ? INSTALLATION_STATUSES : RECLAMATION_STATUSES;

  const visibleTickets = useMemo(() => {
    const source = activeTab === "installation" ? installations : reclamations;
    const query = search.trim().toLowerCase();
    return source.filter((item) => {
      const status =
        activeTab === "installation"
          ? item.workflow_status || "new"
          : item.support_status || "new";
      const matchesStatus = statusFilter === "all" || status === statusFilter;
      const haystack = [
        item.user_name,
        item.user_email,
        item.user_phone,
        item.num_tel,
        item.field_name,
        item.contenu,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return matchesStatus && (!query || haystack.includes(query));
    });
  }, [activeTab, installations, reclamations, search, statusFilter]);

  const formatDate = (value, withTime = false) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return new Intl.DateTimeFormat(i18n.language || "fr", {
      dateStyle: "medium",
      ...(withTime ? { timeStyle: "short" } : {}),
    }).format(date);
  };

  const getStatus = (ticket, type = activeTab) =>
    type === "installation"
      ? ticket.workflow_status || "new"
      : ticket.support_status || "new";

  const openTicket = async (ticket, type = activeTab) => {
    setSelected({ ...ticket, type });
    setDraftStatus(getStatus(ticket, type));
    setScheduledAt(ticket.scheduled_at ? ticket.scheduled_at.slice(0, 16) : "");
    setReply("");
    setVisibility("public");
    setMessageLoading(true);
    try {
      const response = await api.get(`/support/${type}/${ticket.id}/messages`);
      setMessages(response.data || []);
    } catch (error) {
      console.error("Messages loading failed:", error);
      setMessages([]);
    } finally {
      setMessageLoading(false);
    }
  };

  const closeTicket = () => {
    setSelected(null);
    setMessages([]);
    setReply("");
  };

  const saveWorkflow = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.patch(`/admin/support/${selected.type}/${selected.id}`, {
        status: draftStatus,
        ...(selected.type === "installation"
          ? { scheduled_at: scheduledAt || null }
          : {}),
      });
      await loadData(true);
      setSelected((previous) => ({
        ...previous,
        ...(selected.type === "installation"
          ? { workflow_status: draftStatus, scheduled_at: scheduledAt || null }
          : { support_status: draftStatus }),
      }));
      showNotice("success", t("support_status_saved", "Statut mis à jour."));
    } catch (error) {
      console.error("Workflow update failed:", error);
      showNotice(
        "error",
        t("support_save_error", "La modification n’a pas été enregistrée."),
      );
    } finally {
      setSaving(false);
    }
  };

  const sendMessage = async () => {
    if (!selected || !reply.trim() || sending) return;
    setSending(true);
    try {
      const response = await api.post(
        `/support/${selected.type}/${selected.id}/messages`,
        { message: reply.trim(), visibility },
      );
      setMessages((previous) => [...previous, response.data]);
      setReply("");
      showNotice(
        "success",
        visibility === "public"
          ? t("support_reply_sent", "Réponse envoyée au client.")
          : t("support_note_saved", "Note interne enregistrée."),
      );
    } catch (error) {
      console.error("Support reply failed:", error);
      showNotice(
        "error",
        t("support_reply_error", "Impossible d’envoyer le message."),
      );
    } finally {
      setSending(false);
    }
  };

  const addFaq = async (event) => {
    event.preventDefault();
    if (!faqDraft.question.trim() || !faqDraft.answer.trim()) return;
    setFaqSaving(true);
    try {
      const response = await api.post("/admin/add-faq", {
        question: faqDraft.question.trim(),
        answer: faqDraft.answer.trim(),
        is_active: true,
      });
      const created = response.data?.faq;
      if (created) setFaqs((previous) => [created, ...previous]);
      setFaqDraft({ question: "", answer: "" });
      setFaqFormOpen(false);
      showNotice("success", t("faqAddedSuccess", "FAQ ajoutée."));
    } catch (error) {
      console.error("FAQ creation failed:", error);
      showNotice(
        "error",
        t("couldNotSave", "Impossible d’enregistrer la FAQ."),
      );
    } finally {
      setFaqSaving(false);
    }
  };

  const toggleFaq = async (faq) => {
    try {
      await api.put(`/admin/edit-faq/${faq.id}`, { is_active: !faq.is_active });
      setFaqs((previous) =>
        previous.map((item) =>
          item.id === faq.id ? { ...item, is_active: !item.is_active } : item,
        ),
      );
    } catch (error) {
      console.error("FAQ status update failed:", error);
      showNotice("error", t("couldNotUpdateStatus", "Statut non modifié."));
    }
  };

  const tabs = [
    {
      key: "installation",
      icon: RadioTower,
      label: t("support_installations", "Installations"),
    },
    {
      key: "reclamation",
      icon: MessageSquareWarning,
      label: t("support_claims", "Réclamations"),
    },
    {
      key: "faq",
      icon: CircleHelp,
      label: t("support_faq", "Questions fréquentes"),
    },
  ];

  return (
    <main className="sf-support-center" dir={isRtl ? "rtl" : "ltr"}>
      {notice && (
        <div className={`sf-support-notice is-${notice.type}`}>
          {notice.text}
        </div>
      )}

      <header className="sf-support-header">
        <div className="sf-support-heading">
          <span className="sf-support-heading-icon">
            <Headphones size={25} />
          </span>
          <div>
            <span>{t("support_workspace", "ESPACE ADMINISTRATEUR")}</span>
            <h1>{t("support_center_title", "Centre de support")}</h1>
            <p>
              {t(
                "support_center_subtitle",
                "Traitez les demandes et répondez à vos clients depuis un seul endroit.",
              )}
            </p>
          </div>
        </div>
        <button
          className="sf-support-refresh"
          onClick={() => loadData(true)}
          disabled={refreshing}
        >
          <RefreshCw size={17} className={refreshing ? "is-spinning" : ""} />
          {t("refresh", "Actualiser")}
        </button>
      </header>

      <section className="sf-support-stats">
        <article>
          <span className="is-install">
            <RadioTower size={21} />
          </span>
          <div>
            <strong>{counters.installation}</strong>
            <small>
              {t("support_installations_open", "installations à traiter")}
            </small>
          </div>
        </article>
        <article>
          <span className="is-claim">
            <MessageSquareWarning size={21} />
          </span>
          <div>
            <strong>{counters.reclamation}</strong>
            <small>{t("support_claims_open", "réclamations ouvertes")}</small>
          </div>
        </article>
        <article>
          <span className="is-faq">
            <CircleHelp size={21} />
          </span>
          <div>
            <strong>{counters.faq}</strong>
            <small>{t("support_faq_active", "FAQ actives")}</small>
          </div>
        </article>
      </section>

      <section className="sf-support-workspace">
        <nav className="sf-support-tabs">
          {tabs.map(({ key, icon: Icon, label }) => (
            <button
              type="button"
              key={key}
              className={activeTab === key ? "is-active" : ""}
              onClick={() => {
                setActiveTab(key);
                setStatusFilter("all");
                setSearch("");
              }}
            >
              <Icon size={18} />
              <span>{label}</span>
              <b>{counters[key]}</b>
            </button>
          ))}
        </nav>

        {activeTab !== "faq" ? (
          <>
            <div className="sf-support-toolbar">
              <label>
                <Search size={17} />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={t(
                    "support_search_placeholder",
                    "Rechercher un client, un champ ou un numéro...",
                  )}
                />
              </label>
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <option value="all">
                  {t("all_statuses", "Tous les statuts")}
                </option>
                {statusOptions.map((status) => (
                  <option value={status} key={status}>
                    {t(`support_status_${status}`, status)}
                  </option>
                ))}
              </select>
            </div>

            <div className="sf-support-ticket-list">
              {loading ? (
                <div className="sf-support-empty">
                  <LoaderCircle className="is-spinning" />
                  <p>{t("loading", "Chargement...")}</p>
                </div>
              ) : visibleTickets.length === 0 ? (
                <div className="sf-support-empty">
                  <Inbox size={38} />
                  <h3>{t("support_no_ticket", "Aucune demande")}</h3>
                  <p>
                    {t(
                      "support_no_ticket_hint",
                      "Les nouvelles demandes apparaîtront ici.",
                    )}
                  </p>
                </div>
              ) : (
                visibleTickets.map((ticket) => {
                  const status = getStatus(ticket);
                  return (
                    <button
                      className="sf-support-ticket"
                      key={`${activeTab}-${ticket.id}`}
                      onClick={() => openTicket(ticket)}
                    >
                      <span
                        className={`sf-support-ticket-icon is-${activeTab}`}
                      >
                        {activeTab === "installation" ? (
                          <RadioTower size={20} />
                        ) : (
                          <MessageSquareWarning size={20} />
                        )}
                      </span>
                      <span className="sf-support-ticket-main">
                        <strong>
                          {ticket.user_name ||
                            t("unknown_user", "Client inconnu")}
                        </strong>
                        <small>
                          {activeTab === "installation"
                            ? ticket.field_name || t("field", "Champ")
                            : ticket.contenu}
                        </small>
                      </span>
                      <span className="sf-support-ticket-meta">
                        <em className={`is-${status}`}>
                          {t(`support_status_${status}`, status)}
                        </em>
                        <small>
                          <Clock3 size={13} />{" "}
                          {formatDate(ticket.created_at || ticket.date)}
                        </small>
                      </span>
                      <ChevronRight size={19} />
                    </button>
                  );
                })
              )}
            </div>
          </>
        ) : (
          <div className="sf-support-faq-panel">
            <div className="sf-support-faq-head">
              <div>
                <span>
                  {t("support_knowledge_base", "BASE DE CONNAISSANCES")}
                </span>
                <h2>{t("support_manage_faq", "Questions fréquentes")}</h2>
                <p>
                  {t(
                    "support_manage_faq_hint",
                    "Ajoutez les réponses qui seront visibles dans le widget client.",
                  )}
                </p>
              </div>
              <div>
                <button
                  className="sf-support-secondary"
                  onClick={() => navigate("/admin/FAQ")}
                >
                  <ExternalLink size={16} />
                  {t("support_full_management", "Gestion complète")}
                </button>
                <button
                  className="sf-support-primary"
                  onClick={() => setFaqFormOpen((value) => !value)}
                >
                  <Plus size={17} />
                  {t("addFaq", "Ajouter")}
                </button>
              </div>
            </div>
            {faqFormOpen && (
              <form className="sf-support-faq-form" onSubmit={addFaq}>
                <label>
                  {t("question", "Question")}
                  <input
                    value={faqDraft.question}
                    onChange={(event) =>
                      setFaqDraft((draft) => ({
                        ...draft,
                        question: event.target.value,
                      }))
                    }
                    required
                  />
                </label>
                <label>
                  {t("answer", "Réponse")}
                  <textarea
                    rows={4}
                    value={faqDraft.answer}
                    onChange={(event) =>
                      setFaqDraft((draft) => ({
                        ...draft,
                        answer: event.target.value,
                      }))
                    }
                    required
                  />
                </label>
                <div>
                  <button
                    type="button"
                    className="sf-support-secondary"
                    onClick={() => setFaqFormOpen(false)}
                  >
                    {t("cancel", "Annuler")}
                  </button>
                  <button className="sf-support-primary" disabled={faqSaving}>
                    {faqSaving && (
                      <LoaderCircle className="is-spinning" size={16} />
                    )}
                    {t("publish", "Publier")}
                  </button>
                </div>
              </form>
            )}
            <div className="sf-support-faq-grid">
              {faqs.map((faq) => (
                <article
                  key={faq.id}
                  className={!faq.is_active ? "is-inactive" : ""}
                >
                  <div>
                    <CircleHelp size={18} />
                    <span>
                      {faq.is_active
                        ? t("active", "Active")
                        : t("inactive", "Inactive")}
                    </span>
                  </div>
                  <h3>{faq.question}</h3>
                  <p>{faq.answer}</p>
                  <button onClick={() => toggleFaq(faq)}>
                    {faq.is_active
                      ? t("deactivate", "Désactiver")
                      : t("activate", "Activer")}
                  </button>
                </article>
              ))}
            </div>
          </div>
        )}
      </section>

      {selected && (
        <div
          className="sf-support-drawer-layer"
          onMouseDown={(event) =>
            event.target === event.currentTarget && closeTicket()
          }
        >
          <aside className="sf-support-drawer">
            <header>
              <div>
                <span>
                  {selected.type === "installation"
                    ? t(
                        "support_installation_request",
                        "Demande d’installation",
                      )
                    : t("support_claim", "Réclamation")}
                </span>
                <h2>
                  #{selected.id} · {selected.user_name}
                </h2>
              </div>
              <button onClick={closeTicket}>
                <X size={20} />
              </button>
            </header>
            <div className="sf-support-drawer-scroll">
              <section className="sf-support-client-card">
                <div>
                  <UserRound size={18} />
                  <span>
                    <small>{t("client", "Client")}</small>
                    <strong>{selected.user_name || "—"}</strong>
                  </span>
                </div>
                <div>
                  <Mail size={18} />
                  <span>
                    <small>{t("email", "E-mail")}</small>
                    <strong>{selected.user_email || "—"}</strong>
                  </span>
                </div>
                <div>
                  <Phone size={18} />
                  <span>
                    <small>{t("phone", "Téléphone")}</small>
                    <strong>
                      {selected.user_phone || selected.num_tel || "—"}
                    </strong>
                  </span>
                </div>
                {selected.type === "installation" && (
                  <div>
                    <MapPin size={18} />
                    <span>
                      <small>{t("field", "Champ")}</small>
                      <strong>{selected.field_name || "—"}</strong>
                    </span>
                  </div>
                )}
              </section>

              {selected.type === "reclamation" && (
                <section className="sf-support-original-message">
                  <span>{t("client_message", "Message du client")}</span>
                  <p>{selected.contenu}</p>
                </section>
              )}

              <section className="sf-support-workflow-card">
                <div className="sf-support-section-title">
                  <CheckCircle2 size={18} />
                  <div>
                    <h3>{t("support_processing", "Traitement")}</h3>
                    <p>
                      {t(
                        "support_processing_hint",
                        "Mettez à jour l’avancement visible par le client.",
                      )}
                    </p>
                  </div>
                </div>
                <label>
                  {t("status", "Statut")}
                  <select
                    value={draftStatus}
                    onChange={(event) => setDraftStatus(event.target.value)}
                  >
                    {(selected.type === "installation"
                      ? INSTALLATION_STATUSES
                      : RECLAMATION_STATUSES
                    ).map((status) => (
                      <option key={status} value={status}>
                        {t(`support_status_${status}`, status)}
                      </option>
                    ))}
                  </select>
                </label>
                {selected.type === "installation" && (
                  <label>
                    {t("support_appointment", "Date du rendez-vous")}
                    <div className="sf-support-date-input">
                      <CalendarDays size={17} />
                      <input
                        type="datetime-local"
                        value={scheduledAt}
                        onChange={(event) => setScheduledAt(event.target.value)}
                      />
                    </div>
                  </label>
                )}
                <button
                  className="sf-support-primary is-wide"
                  onClick={saveWorkflow}
                  disabled={saving}
                >
                  {saving && <LoaderCircle className="is-spinning" size={16} />}
                  {t("save_status", "Enregistrer le suivi")}
                </button>
              </section>

              <section className="sf-support-conversation">
                <div className="sf-support-section-title">
                  <Send size={18} />
                  <div>
                    <h3>{t("support_conversation", "Conversation")}</h3>
                    <p>
                      {t(
                        "support_conversation_hint",
                        "Les réponses publiques sont visibles par le client.",
                      )}
                    </p>
                  </div>
                </div>
                <div className="sf-support-messages">
                  {messageLoading ? (
                    <LoaderCircle className="is-spinning" />
                  ) : messages.length === 0 ? (
                    <p className="sf-support-no-message">
                      {t("support_no_message", "Aucun message pour le moment.")}
                    </p>
                  ) : (
                    messages.map((message) => (
                      <article
                        key={message.id}
                        className={`is-${message.sender_role} ${message.visibility === "internal" ? "is-internal" : ""}`}
                      >
                        <div>
                          <strong>
                            {message.sender_role === "admin"
                              ? message.sender_name ||
                                t("support_team", "Équipe SmartFarm")
                              : selected.user_name}
                          </strong>
                          {message.visibility === "internal" && (
                            <em>
                              <StickyNote size={12} />
                              {t("internal_note", "Note interne")}
                            </em>
                          )}
                          <time>{formatDate(message.created_at, true)}</time>
                        </div>
                        <p>{message.message}</p>
                      </article>
                    ))
                  )}
                </div>
                <div className="sf-support-compose">
                  <div className="sf-support-visibility">
                    <button
                      className={visibility === "public" ? "is-active" : ""}
                      onClick={() => setVisibility("public")}
                    >
                      <Send size={14} />
                      {t("public_reply", "Réponse client")}
                    </button>
                    <button
                      className={visibility === "internal" ? "is-active" : ""}
                      onClick={() => setVisibility("internal")}
                    >
                      <StickyNote size={14} />
                      {t("internal_note", "Note interne")}
                    </button>
                  </div>
                  <textarea
                    rows={4}
                    value={reply}
                    onChange={(event) => setReply(event.target.value)}
                    placeholder={
                      visibility === "public"
                        ? t(
                            "support_reply_placeholder",
                            "Écrire une réponse au client...",
                          )
                        : t(
                            "support_note_placeholder",
                            "Ajouter une note pour l’équipe...",
                          )
                    }
                  />
                  <button
                    className="sf-support-primary"
                    onClick={sendMessage}
                    disabled={!reply.trim() || sending}
                  >
                    {sending ? (
                      <LoaderCircle className="is-spinning" size={16} />
                    ) : (
                      <Send size={16} />
                    )}
                    {visibility === "public"
                      ? t("send_reply", "Envoyer la réponse")
                      : t("save_note", "Enregistrer la note")}
                  </button>
                </div>
              </section>
            </div>
          </aside>
        </div>
      )}
    </main>
  );
};

export default SupportCenter;
